<template>
  <div class="content">
    <div class="md-layout">
      <div
        :class="`md-layout-item md-medium-size-100 md-xsmall-size-100` + [isViewerAdmin ? 'md-size-100' : 'md-size-70']"
      >
        <md-card>
          <md-card-header data-background-color="purple" class="md-layout md-alignment-center-space-between">
            <div>
              <h4 class="title">Applications</h4>
              <p v-if="totalApplications === 1" class="category">{{ totalApplications }} Application</p>
              <p v-else class="category">{{ totalApplications }} Applications</p>
            </div>
            <div>
              <on-off-button
                mode="switch"
                switch-text="Hide/Show deleted"
                @click="showHideDeletedApplications"
              ></on-off-button>
            </div>
          </md-card-header>
          <md-card-content>
            <md-table v-model="applications" table-header-color="purple" md-card>
              <md-table-row
                slot="md-table-row"
                class="cursor-pointer"
                slot-scope="{ item }"
                @click="gotoAppDetails(item.id)"
              >
                <md-table-cell md-label="Name">{{ item.name }} </md-table-cell>
                <md-table-cell md-label="Description"
                  >{{ getSubstring(item.description) }}
                  <md-tooltip md-direction="top">{{ item.description }} </md-tooltip>
                </md-table-cell>
                <md-table-cell md-label="Users"> {{ item.activeUsers }} </md-table-cell>
                <md-table-cell md-label="Is active?" class="is-active">
                  <md-icon v-if="!item.isDeleted">done</md-icon>
                  <md-icon v-else>remove</md-icon>
                </md-table-cell>
                <md-table-cell v-if="!item.isDeleted && !isViewerAdmin" md-label="Actions" class="is-active">
                  <md-button
                    v-if="!item.isDeleted && !isViewerAdmin"
                    class="md-icon-button"
                    @click.stop="deleteApplication(item.id)"
                  >
                    <md-tooltip md-direction="top">Delete Application</md-tooltip>
                    <md-icon>delete</md-icon>
                  </md-button>
                </md-table-cell>
              </md-table-row>
            </md-table>
            <div class="md-layout md-alignment-center">
              <pagination v-model="applicationsPage" :page-count="totalPages" @input="changePage"></pagination>
            </div>
          </md-card-content>
        </md-card>
      </div>
      <div v-if="!isViewerAdmin" class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-30">
        <md-card>
          <md-card-header data-background-color="green">
            <h4 class="title">Shortcuts</h4>
          </md-card-header>
          <md-card-content>
            <md-list-item>
              <md-avatar>
                <img src="../../assets/img/application_logos/api_logo.jpg" alt="logo" />
              </md-avatar>
              <md-content class="md-layout-item">Auth API</md-content>
              <md-button @click="showAddApplication = true" class="md-icon-button md-dense">
                <md-icon>add</md-icon>
              </md-button>
            </md-list-item>
          </md-card-content>
        </md-card>
      </div>
      <create-new-application
        v-if="!isViewerAdmin"
        :show="showAddApplication"
        @close="showAddApplication = false"
      ></create-new-application>

      <delete-dialog
        :show="showDeleteDialog"
        delete-api-name="deleteApplication"
        title="Delete Application"
        message="This action deletes this application permanently, Are you sure?"
        :request-body="{ application_pk: deleteDialogKey }"
        @close="
          showDeleteDialog = false;
          deleteDialogKey = null;
        "
        @deleted="
          fetchApplications();
          showDeleteDialog = false;
          deleteDialogKey = null;
        "
        v-if="!isViewerAdmin"
      ></delete-dialog>
    </div>
  </div>
</template>
<script>
import fetchErrorHandler from "../../error.js";

import { Pagination } from "@/components";
import CreateNewApplication from "../../components/CreateNewApplication";
import DeleteDialog from "../../components/DeleteDialog";
import OnOffButton from "@/components/OnOffButton";
import snakeCaseObjectKeysToCamelCase from "../../utils/snakeCaseObjectKeysToCamelCase";
import { computed, onMounted, ref } from "@vue/composition-api";
import apiManager from "../../api-manager";

export default {
  name: "applications-list",

  components: {
    OnOffButton,
    CreateNewApplication,
    Pagination,
    DeleteDialog,
  },

  setup(props, { root }) {
    const applications = ref([]);
    const filters = ref({});
    const totalApplications = ref(0);
    const fetchApplications = async () => {
      const response = await apiManager.getApplications({ params: filters.value });

      if (response.status === 200) {
        applications.value = response.parsedData.applications.map((item) => snakeCaseObjectKeysToCamelCase(item));

        totalPages.value = snakeCaseObjectKeysToCamelCase(response.parsedData).totalPages;
        totalApplications.value = snakeCaseObjectKeysToCamelCase(response.parsedData).totalItems;
      } else {
        fetchErrorHandler(response.status, response.statusText, root.$options.name);
      }
    };

    const showAddApplication = ref(false);  // Add application dialog

    const gotoAppDetails = (id) => {
      root.$router.push("applications/application-details/?application_id=" + id);
    };

    const applicationsPage = ref(1);
    const totalPages = ref(1);
    const changePage = (page) => {
      if (filters.value["page"] !== page) {
        filters.value["page"] = page;
        fetchApplications();
      }
    };

    const showDeletedApplications = ref(false);
    const showHideDeletedApplications = () => {
      showDeletedApplications.value = !showDeletedApplications.value;
      filters.value = { ...filters.value, active_only: !showDeletedApplications.value };
      fetchApplications();
    };

    const showDeleteDialog = ref(false);
    const deleteDialogKey = ref(null);
    const deleteApplication = (application_pk) => {
      showDeleteDialog.value = true;
      deleteDialogKey.value = application_pk;
    };

    const getSubstring = (str) => {
      return str.length > 20 ? `${str.substring(0, 20)} ...` : str;
    };

    const isViewerAdmin = computed(() => {
      return window.localStorage.getItem("adminGroups").includes("viewer_admins");
    });

    // Startup
    onMounted(() => {
      fetchApplications();
    });

    return {
      applications,
      filters,
      totalApplications,

      fetchApplications,

      showAddApplication,

      gotoAppDetails,

      applicationsPage,
      totalPages,
      changePage,

      showDeletedApplications,
      showHideDeletedApplications,

      deleteApplication,
      showDeleteDialog,
      deleteDialogKey,

      getSubstring,

      isViewerAdmin,
    };
  },
};
</script>

<style lang="scss" scoped>
.is-active {
  .md-icon {
    margin-left: 16px;
    margin-right: 16px;
  }
}
</style>
